/**
 * AI interview languages are configured in the backend (MongoDB `ai_interview_languages`)
 * and served by the `/realtime-interview/languages` endpoints. Nothing here lists the
 * supported languages — only the English fallback used when the API is unavailable.
 *
 * No prompt text lives in the frontend: everything sent to the AI model comes from the
 * backend in the session's `runtime_instructions`.
 */
export interface InterviewLanguage {
	code: string;
	name: string;
	native_name: string;
	display_name?: string;
	is_rtl?: boolean;
	is_mixed?: boolean;
	accept_as_input?: boolean;
	script_ranges?: string[];
}

/** Prompt text rendered by the backend for this session; the frontend only forwards it to the realtime model. */
export interface InterviewRuntimeInstructions {
	/** Hiring panel: instruction for the interviewer's opening turn. */
	greeting?: string | null;
	/** Hiring panel: system message sent `time_warning_seconds` before the interview ends. */
	time_warning?: string | null;
	time_warning_seconds?: number | null;
	/** Hiring panel: sent once after the English-communication reminder (English interviews only). */
	language_reminder_ack?: string | null;
	/** Hiring panel: requests the voice-analysis JSON at the end of the interview. */
	voice_analysis_request?: string | null;
	/** Student interview: instruction used when the candidate answers in an unsupported language. */
	language_block?: string | null;
}

export const DEFAULT_INTERVIEW_LANGUAGE: InterviewLanguage = {
	code: 'en',
	name: 'English',
	native_name: 'English',
	display_name: 'English',
	is_rtl: false,
	is_mixed: false,
	accept_as_input: true,
	script_ranges: []
};

/** Fallback when the languages API fails: English only, so the interview can still proceed. */
export const FALLBACK_INTERVIEW_LANGUAGES: InterviewLanguage[] = [DEFAULT_INTERVIEW_LANGUAGE];

/** ['English', 'Hindi', 'Tamil'] -> 'English, Hindi, and Tamil' */
export function joinLanguageNames(names: string[], conjunction: 'and' | 'or'): string {
	if (names.length <= 1) return names[0] ?? '';
	if (names.length === 2) return `${names[0]} ${conjunction} ${names[1]}`;
	return `${names.slice(0, -1).join(', ')}, ${conjunction} ${names[names.length - 1]}`;
}

/** Parses configured ranges like "0900-097F" into numeric code point ranges. */
export function parseScriptRanges(languages: InterviewLanguage[]): [number, number][] {
	return languages
		.flatMap(lang => lang.script_ranges ?? [])
		.map(range => range.split('-').map(part => parseInt(part, 16)))
		.filter(([start, end]) => !isNaN(start) && !isNaN(end))
		.map(([start, end]) => [start, end] as [number, number]);
}
